import styled from "styled-components";
import { accentColor, bodyTextAccentColor, bodyTextRegular } from "../layout/style";
import { Section, AccentedText } from "../layout/style";

const ContentContainer = styled.div`
    width: 100%;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 50px;
    margin-top: 0px;
margin-bottom: 50px;
    ${bodyTextRegular}
`;

const StudiesContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    padding: 0px;

    justify-content: space-evenly;
    margin-bottom: 30px;
`;

const StudyColumn = styled.div`
    flex: 1 0;
    display: flex;
    flex-direction: column;
    padding: 10px;
`;

const StudyHeader = styled.h2`
    font-size: 1.2rem;
    color: ${accentColor};
    border-bottom: 1px solid #CCCCCC;
    padding-bottom: 5px;
    margin: 0px;
    margin-bottom: 5px;

    position: relative;

    &:after {
        position: absolute;
        right: 0px;
        text-align: right;
        font-size: 1.5rem;
        content: "→";
    }

    &.last:after {
        display: none;
    }
`;

const StudyText = styled.p`
    font-size: 1rem;
`;

const RestyledSection = styled(Section)`
    padding-left: 0px;

    & p {
        margin: 8px 0px;

    }

`

const TopRowContentContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin: 0px;

    & p {
        flex: 1 0 50%;
        width: 50%;
    }
`;

/* Sequence complete list
Structure: skin, muscle, bone
Orientation: pitch, yaw, roll, translation
Asymmetry: Freedom of movement in shoulder
5th degree of freedom: Neck
Locomotion: Change in center of mass
Combination: Adding spiral and vector
Oppositional direction: pulling and levering


*/

const StudyInfoUl = styled.ul`
    list-style-type: none;
    margin: 0px;
    padding: 0px;
`;

const StudyInfoLi = styled.li`
    margin: 0px 0px 5px 0px;
    padding: 0px;
    color: #666666;

    &.hidden {
        display: none;
    }

    &.highlight {
        color: #bb910c;
        position: relative;
    }

    &.highlight:before {
        display: block;
        position: absolute;
        left: -20px;
        content: "+";
    }
`;


const StudyInfoList = ({ studyNumber }) => {

    let className1 = `${studyNumber < 1 ? "hidden" : ""} ${studyNumber == 1 ? "highlight" : ""}`;
    let className2 = `${studyNumber < 2 ? "hidden" : ""} ${studyNumber == 2 ? "highlight" : ""}`;
    let className3 = `${studyNumber < 3 ? "hidden" : ""} ${studyNumber == 3 ? "highlight" : ""}`;
    let className4 = `${studyNumber < 4 ? "hidden" : ""} ${studyNumber == 4 ? "highlight" : ""}`;
    let className5 = `${studyNumber < 5 ? "hidden" : ""} ${studyNumber == 5 ? "highlight" : ""}`;
    let className6 = `${studyNumber < 6 ? "hidden" : ""} ${studyNumber == 6 ? "highlight" : ""}`;
    let className7 = `${studyNumber < 7 ? "hidden" : ""} ${studyNumber == 7 ? "highlight" : ""}`;

    return <StudyInfoUl>

        <StudyInfoLi className={className1}><b>Structure</b>: skin, muscle, bone</StudyInfoLi>
        <StudyInfoLi className={className2}><b>Orientation</b>: pitch, yaw, roll, translation</StudyInfoLi>
        <StudyInfoLi className={className3}><b>Asymmetry</b>: freedom of movement in shoulder</StudyInfoLi>
        <StudyInfoLi className={className4}><b>5th degree of freedom</b>: movement in neck</StudyInfoLi>
        <StudyInfoLi className={className5}><b>Locomotor</b>: change in center of pressure</StudyInfoLi>
        <StudyInfoLi className={className6}><b>Combination</b>: adding spiral and vector</StudyInfoLi>
        <StudyInfoLi className={className7}><b>Oppositional direction</b>: pulling and levering</StudyInfoLi>
    </StudyInfoUl>

}

const LoopSlide = () => {


    return <ContentContainer>
        <RestyledSection>
            <TopRowContentContainer>
                <p>As a whole, the creative project is intended as its own provocation to consider not only the limits of what can be captured through technology, but also how the fidelity of what is captured may be compromised when moving across kinesthetic, computational, visual, and musical representations.
                    Thus, the title “<i>Proxies</i>” is a gesture towards investigations of these constraints, and reimagines the assumptions of one-to-one correspondence between data and phenomena that structure our encounters with technology in embodied experience.</p>
                <p>One of these investigations is an inquiry into and critique of the assumption that data can be a “stand-in” and an approximation of ourselves. Another is an attempt to render visible what nuance of lived experience may be lost in our removal from it through datafied representation. This serves as a way to illuminate the embodied relationships that data are meant to represent but fail to do so in high fidelity — that is, what dimensions of embodied experience may be lost if we care more about data than real humans.
                </p>

            </TopRowContentContainer>
            <p>To explore this, the choreography of <i>Proxies</i> is structured around surfacing subtlety in kinesthetic experience, much of which may be invisible from the outside unless captured and represented. In the opening duet, two dancers proceed through a series of movement studies in sequence. Each study invites the dancers to negotiate shared movement under a different set of constraints and conditions, beginning with a study focused on maintaining equilibrium in applied pressure. As each study progresses, these constraints are gradually removed, and new possibilities of movement are opened up. As this happens, additional information about the system of bodies moving in interaction becomes available to the dancers – information communicated through skin, muscle, and bone first, and expanded to incorporate additional degrees of freedom in movement by the end of the series.</p>
        </RestyledSection>
        <StudiesContainer>
            <StudyColumn>
                <StudyHeader>Study 1</StudyHeader>
                <StudyInfoList studyNumber={1} />
            </StudyColumn>
            <StudyColumn>
                <StudyHeader>Study 2</StudyHeader>
                <StudyInfoList studyNumber={2} />
            </StudyColumn>
            <StudyColumn>
                <StudyHeader>Study 3</StudyHeader>
                <StudyInfoList studyNumber={3} />
            </StudyColumn>
            <StudyColumn>
                <StudyHeader>Study 4</StudyHeader>
                <StudyInfoList studyNumber={4} />
            </StudyColumn>
            <StudyColumn>
                <StudyHeader>Study 5</StudyHeader>
                <StudyInfoList studyNumber={5} />
            </StudyColumn>
            <StudyColumn>
                <StudyHeader>Study 6</StudyHeader>
                <StudyInfoList studyNumber={6} />
            </StudyColumn>
            <StudyColumn>
                <StudyHeader className="last">Study 7</StudyHeader>
                <StudyInfoList studyNumber={7} />
            </StudyColumn>
        </StudiesContainer>

    </ContentContainer>;
}

export default LoopSlide;